<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import KnowledgebasesAPI from 'dashboard/api/knowledgebases';

const props = defineProps({
  knowledgebaseId: {
    type: String,
    required: true,
  },
});

const { t } = useI18n();

const activeTab = ref('run');
const tabs = [
  { key: 'run', label: () => t('LIBRARIES.EVAL.TABS.RUN') },
  { key: 'query_builder', label: () => t('LIBRARIES.EVAL.TABS.QUERY_BUILDER') },
  { key: 'stored', label: () => t('LIBRARIES.EVAL.TABS.STORED') },
  { key: 'run_stored', label: () => t('LIBRARIES.EVAL.TABS.RUN_STORED') },
];

// Run Eval tab
const evalForm = ref({
  query: '',
  max_results: 10,
  method: 'static_contains',
  value: '',
  field: 'page_content',
  max_docs_to_evaluate: 5,
});
const evalResult = ref(null);
const isRunningEval = ref(false);

// Query Builder tab
const qbForm = ref({
  query: '',
  expected_filter: '{}',
});
const qbResult = ref(null);
const isRunningQb = ref(false);

// Stored Evals tab
const storedEvals = ref([]);
const isFetchingStored = ref(false);
const showCreateForm = ref(false);
const editingEval = ref(null);
const storedEvalForm = ref({
  name: '',
  description: '',
  query: '',
  max_results: 10,
  method: 'static_contains',
  value: '',
  field: 'page_content',
});
const isSavingEval = ref(false);

// Run Stored tab
const runResults = ref(null);
const isRunningStored = ref(false);
const selectedEvalIds = ref([]);

const methodOptions = [
  { value: 'static_contains', label: 'Static Contains' },
  { value: 'static_equals', label: 'Static Equals' },
  { value: 'llm_eval', label: 'LLM Eval' },
];

const buildExpectedOutcome = form => ({
  method: form.method,
  value:
    form.method === 'static_contains'
      ? form.value
          .split('\n')
          .map(v => v.trim())
          .filter(Boolean)
      : form.value,
  field: form.field,
  ...(form.method === 'llm_eval'
    ? { max_docs_to_evaluate: form.max_docs_to_evaluate }
    : {}),
});

const runEval = async () => {
  if (!evalForm.value.query) return;
  isRunningEval.value = true;
  evalResult.value = null;
  try {
    const response = await KnowledgebasesAPI.runEval(props.knowledgebaseId, {
      query: evalForm.value.query,
      max_results: evalForm.value.max_results,
      expected_outcome: buildExpectedOutcome(evalForm.value),
    });
    evalResult.value = response.data;
  } catch {
    evalResult.value = { error: t('LIBRARIES.EVAL.ERROR') };
  } finally {
    isRunningEval.value = false;
  }
};

const runQueryBuilderEval = async () => {
  if (!qbForm.value.query) return;
  isRunningQb.value = true;
  qbResult.value = null;
  try {
    let expectedFilter = {};
    try {
      expectedFilter = JSON.parse(qbForm.value.expected_filter);
    } catch {
      qbResult.value = { error: t('LIBRARIES.EVAL.INVALID_JSON') };
      return;
    }
    const response = await KnowledgebasesAPI.runQueryBuilderEval(
      props.knowledgebaseId,
      {
        query: qbForm.value.query,
        expected_filter: expectedFilter,
      }
    );
    qbResult.value = response.data;
  } catch {
    qbResult.value = { error: t('LIBRARIES.EVAL.ERROR') };
  } finally {
    isRunningQb.value = false;
  }
};

const fetchStoredEvals = async () => {
  isFetchingStored.value = true;
  try {
    const response = await KnowledgebasesAPI.listStoredEvals(
      props.knowledgebaseId
    );
    storedEvals.value = response.data?.evals || [];
  } catch {
    storedEvals.value = [];
  } finally {
    isFetchingStored.value = false;
  }
};

const resetStoredEvalForm = () => {
  storedEvalForm.value = {
    name: '',
    description: '',
    query: '',
    max_results: 10,
    method: 'static_contains',
    value: '',
    field: 'page_content',
  };
};

const openCreateForm = () => {
  editingEval.value = null;
  resetStoredEvalForm();
  showCreateForm.value = true;
};

const openEditForm = evalItem => {
  editingEval.value = evalItem;
  const outcome = evalItem.expected_outcome || {};
  storedEvalForm.value = {
    name: evalItem.name || '',
    description: evalItem.description || '',
    query: evalItem.query || '',
    max_results: evalItem.max_results || 10,
    method: outcome.method || 'static_contains',
    value: Array.isArray(outcome.value)
      ? outcome.value.join('\n')
      : outcome.value || '',
    field: outcome.field || 'page_content',
  };
  showCreateForm.value = true;
};

const cancelForm = () => {
  showCreateForm.value = false;
  editingEval.value = null;
  resetStoredEvalForm();
};

const saveStoredEval = async () => {
  if (!storedEvalForm.value.name || !storedEvalForm.value.query) return;
  isSavingEval.value = true;
  const payload = {
    name: storedEvalForm.value.name,
    description: storedEvalForm.value.description,
    query: storedEvalForm.value.query,
    max_results: storedEvalForm.value.max_results,
    expected_outcome: buildExpectedOutcome(storedEvalForm.value),
  };
  try {
    if (editingEval.value) {
      await KnowledgebasesAPI.updateStoredEval(
        props.knowledgebaseId,
        editingEval.value.id,
        payload
      );
    } else {
      await KnowledgebasesAPI.createStoredEval(props.knowledgebaseId, payload);
    }
    cancelForm();
    fetchStoredEvals();
  } catch {
    // silently fail on save
  } finally {
    isSavingEval.value = false;
  }
};

const deleteStoredEval = async evalItem => {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(t('LIBRARIES.EVAL.DELETE_CONFIRM', { name: evalItem.name }))
  )
    return;
  try {
    await KnowledgebasesAPI.deleteStoredEval(
      props.knowledgebaseId,
      evalItem.id
    );
    fetchStoredEvals();
  } catch {
    // silently fail on delete
  }
};

const runStoredEvals = async () => {
  isRunningStored.value = true;
  runResults.value = null;
  try {
    const evalIds =
      selectedEvalIds.value.length > 0 ? selectedEvalIds.value : null;
    const response = await KnowledgebasesAPI.runStoredEvals(
      props.knowledgebaseId,
      evalIds
    );
    runResults.value = response.data;
  } catch {
    runResults.value = { error: t('LIBRARIES.EVAL.ERROR') };
  } finally {
    isRunningStored.value = false;
  }
};

const toggleSelectEval = evalId => {
  const idx = selectedEvalIds.value.indexOf(evalId);
  if (idx > -1) {
    selectedEvalIds.value.splice(idx, 1);
  } else {
    selectedEvalIds.value.push(evalId);
  }
};

onMounted(() => {
  fetchStoredEvals();
});
</script>

<template>
  <div class="flex-1 h-full flex flex-col pt-6">
    <div class="flex-1 h-full flex flex-col overflow-auto">
      <div
        class="bg-slate-25 dark:bg-slate-900 pt-4 pb-0 px-8 border-b border-solid border-slate-50 dark:border-slate-800/50"
      >
        <h2
          class="text-2xl text-slate-800 dark:text-slate-100 mb-3 font-medium"
        >
          {{ $t('LIBRARIES.EVAL.TITLE') }}
        </h2>
        <!-- Tab navigation -->
        <div class="flex gap-1">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            class="px-4 py-2 text-sm font-medium rounded-t-md transition-colors"
            :class="
              activeTab === tab.key
                ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-b-0 border-slate-200 dark:border-slate-700'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            "
            @click="activeTab = tab.key"
          >
            {{ tab.label() }}
          </button>
        </div>
      </div>

      <div class="p-6 flex-1 overflow-auto">
        <!-- Run Eval Tab -->
        <div v-if="activeTab === 'run'">
          <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
            {{ $t('LIBRARIES.EVAL.RUN_DESCRIPTION') }}
          </p>
          <form class="space-y-4" @submit.prevent="runEval">
            <div>
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                {{ $t('LIBRARIES.EVAL.QUERY') }}
              </label>
              <input
                v-model="evalForm.query"
                type="text"
                class="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                :placeholder="$t('LIBRARIES.EVAL.QUERY_PLACEHOLDER')"
              />
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label
                  class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                >
                  {{ $t('LIBRARIES.EVAL.METHOD') }}
                </label>
                <select
                  v-model="evalForm.method"
                  class="reset-base w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                >
                  <option
                    v-for="opt in methodOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </div>
              <div>
                <label
                  class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                >
                  {{ $t('LIBRARIES.EVAL.FIELD') }}
                </label>
                <input
                  v-model="evalForm.field"
                  type="text"
                  class="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                />
              </div>
            </div>
            <div>
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                {{ $t('LIBRARIES.EVAL.EXPECTED_VALUE') }}
                <span
                  v-if="evalForm.method === 'static_contains'"
                  class="text-slate-400 font-normal ml-1"
                >
                  {{ $t('LIBRARIES.EVAL.ONE_PER_LINE') }}
                </span>
              </label>
              <textarea
                v-model="evalForm.value"
                rows="3"
                class="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono text-sm"
                :placeholder="
                  evalForm.method === 'llm_eval'
                    ? $t('LIBRARIES.EVAL.LLM_VALUE_PLACEHOLDER')
                    : $t('LIBRARIES.EVAL.VALUE_PLACEHOLDER')
                "
              />
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label
                  class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                >
                  {{ $t('LIBRARIES.EVAL.MAX_RESULTS') }}
                </label>
                <input
                  v-model.number="evalForm.max_results"
                  type="number"
                  min="1"
                  max="20"
                  class="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                />
              </div>
              <div v-if="evalForm.method === 'llm_eval'">
                <label
                  class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                >
                  {{ $t('LIBRARIES.EVAL.MAX_DOCS_TO_EVAL') }}
                </label>
                <input
                  v-model.number="evalForm.max_docs_to_evaluate"
                  type="number"
                  min="1"
                  max="20"
                  class="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                />
              </div>
            </div>
            <div class="flex justify-end">
              <NextButton
                :is-loading="isRunningEval"
                :label="$t('LIBRARIES.EVAL.RUN_BUTTON')"
              />
            </div>
          </form>

          <!-- Eval Result -->
          <div v-if="evalResult" class="mt-6">
            <div
              v-if="evalResult.error"
              class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-700 dark:text-red-300"
            >
              {{ evalResult.error }}
            </div>
            <div v-else class="space-y-3">
              <div
                class="flex items-center gap-3 p-4 rounded-md border"
                :class="
                  evalResult.passed
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                "
              >
                <span
                  class="text-2xl"
                  :aria-label="evalResult.passed ? 'passed' : 'failed'"
                >
                  {{ evalResult.passed ? '✓' : '✗' }}
                </span>
                <div>
                  <p
                    class="font-semibold"
                    :class="
                      evalResult.passed
                        ? 'text-green-800 dark:text-green-200'
                        : 'text-red-800 dark:text-red-200'
                    "
                  >
                    {{
                      evalResult.passed
                        ? $t('LIBRARIES.EVAL.PASSED')
                        : $t('LIBRARIES.EVAL.FAILED')
                    }}
                  </p>
                  <p class="text-sm text-slate-600 dark:text-slate-400">
                    {{
                      $t('LIBRARIES.EVAL.CONFIDENCE', {
                        value: Math.round((evalResult.confidence || 0) * 100),
                      })
                    }}
                  </p>
                </div>
              </div>
              <details
                class="border border-slate-200 dark:border-slate-700 rounded-md"
              >
                <summary
                  class="px-4 py-2 cursor-pointer text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  {{ $t('LIBRARIES.EVAL.DETAILS') }}
                </summary>
                <pre
                  class="p-4 text-xs bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 overflow-auto rounded-b-md"
                  >{{ JSON.stringify(evalResult, null, 2) }}</pre>
              </details>
            </div>
          </div>
        </div>

        <!-- Query Builder Eval Tab -->
        <div v-else-if="activeTab === 'query_builder'">
          <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
            {{ $t('LIBRARIES.EVAL.QB_DESCRIPTION') }}
          </p>
          <form class="space-y-4" @submit.prevent="runQueryBuilderEval">
            <div>
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                {{ $t('LIBRARIES.EVAL.QUERY') }}
              </label>
              <input
                v-model="qbForm.query"
                type="text"
                class="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                :placeholder="$t('LIBRARIES.EVAL.QUERY_PLACEHOLDER')"
              />
            </div>
            <div>
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                {{ $t('LIBRARIES.EVAL.EXPECTED_FILTER') }}
              </label>
              <textarea
                v-model="qbForm.expected_filter"
                rows="5"
                class="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono text-sm"
                placeholder="{}"
              />
            </div>
            <div class="flex justify-end">
              <NextButton
                :is-loading="isRunningQb"
                :label="$t('LIBRARIES.EVAL.RUN_BUTTON')"
              />
            </div>
          </form>

          <div v-if="qbResult" class="mt-6">
            <div
              v-if="qbResult.error"
              class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-700 dark:text-red-300"
            >
              {{ qbResult.error }}
            </div>
            <div v-else class="space-y-3">
              <div
                class="flex items-center gap-3 p-4 rounded-md border"
                :class="
                  qbResult.passed
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                "
              >
                <span class="text-2xl">{{ qbResult.passed ? '✓' : '✗' }}</span>
                <div>
                  <p
                    class="font-semibold"
                    :class="
                      qbResult.passed
                        ? 'text-green-800 dark:text-green-200'
                        : 'text-red-800 dark:text-red-200'
                    "
                  >
                    {{
                      qbResult.passed
                        ? $t('LIBRARIES.EVAL.PASSED')
                        : $t('LIBRARIES.EVAL.FAILED')
                    }}
                  </p>
                  <p class="text-sm text-slate-600 dark:text-slate-400">
                    {{
                      $t('LIBRARIES.EVAL.CONFIDENCE', {
                        value: Math.round((qbResult.confidence || 0) * 100),
                      })
                    }}
                  </p>
                </div>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <p class="text-xs font-medium text-slate-500 mb-1">
                    {{ $t('LIBRARIES.EVAL.GENERATED_FILTER') }}
                  </p>
                  <pre
                    class="p-3 text-xs bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 rounded border border-slate-200 dark:border-slate-700 overflow-auto"
                    >{{
                      JSON.stringify(qbResult.generated_filter, null, 2)
                    }}</pre>
                </div>
                <div>
                  <p class="text-xs font-medium text-slate-500 mb-1">
                    {{ $t('LIBRARIES.EVAL.EXPECTED_FILTER') }}
                  </p>
                  <pre
                    class="p-3 text-xs bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 rounded border border-slate-200 dark:border-slate-700 overflow-auto"
                    >{{
                      JSON.stringify(qbResult.expected_filter, null, 2)
                    }}</pre>
                </div>
              </div>
              <div
                v-if="qbResult.differences && qbResult.differences.length"
                class="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-md"
              >
                <p
                  class="text-xs font-medium text-amber-700 dark:text-amber-300 mb-1"
                >
                  {{ $t('LIBRARIES.EVAL.DIFFERENCES') }}
                </p>
                <ul
                  class="list-disc list-inside text-xs text-amber-600 dark:text-amber-400"
                >
                  <li v-for="(diff, i) in qbResult.differences" :key="i">
                    {{ diff }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Stored Evals Tab -->
        <div v-else-if="activeTab === 'stored'">
          <div class="flex justify-between items-center mb-4">
            <p class="text-sm text-slate-500 dark:text-slate-400">
              {{ $t('LIBRARIES.EVAL.STORED_DESCRIPTION') }}
            </p>
            <NextButton
              v-if="!showCreateForm"
              icon="i-lucide-plus"
              :label="$t('LIBRARIES.EVAL.ADD_EVAL')"
              @click="openCreateForm"
            />
          </div>

          <!-- Create/Edit Form -->
          <div
            v-if="showCreateForm"
            class="mb-6 p-4 border border-slate-200 dark:border-slate-700 rounded-md bg-slate-50 dark:bg-slate-800/50"
          >
            <h3
              class="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-4"
            >
              {{
                editingEval
                  ? $t('LIBRARIES.EVAL.EDIT_EVAL')
                  : $t('LIBRARIES.EVAL.CREATE_EVAL')
              }}
            </h3>
            <form class="space-y-3" @submit.prevent="saveStoredEval">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label
                    class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1"
                  >
                    {{ $t('LIBRARIES.EVAL.NAME') }}
                  </label>
                  <input
                    v-model="storedEvalForm.name"
                    type="text"
                    required
                    class="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label
                    class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1"
                  >
                    {{ $t('LIBRARIES.EVAL.DESCRIPTION') }}
                  </label>
                  <input
                    v-model="storedEvalForm.description"
                    type="text"
                    class="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  />
                </div>
              </div>
              <div>
                <label
                  class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1"
                >
                  {{ $t('LIBRARIES.EVAL.QUERY') }}
                </label>
                <input
                  v-model="storedEvalForm.query"
                  type="text"
                  required
                  class="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                />
              </div>
              <div class="grid grid-cols-3 gap-4">
                <div>
                  <label
                    class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1"
                  >
                    {{ $t('LIBRARIES.EVAL.METHOD') }}
                  </label>
                  <select
                    v-model="storedEvalForm.method"
                    class="reset-base w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  >
                    <option
                      v-for="opt in methodOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </div>
                <div>
                  <label
                    class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1"
                  >
                    {{ $t('LIBRARIES.EVAL.FIELD') }}
                  </label>
                  <input
                    v-model="storedEvalForm.field"
                    type="text"
                    class="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label
                    class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1"
                  >
                    {{ $t('LIBRARIES.EVAL.MAX_RESULTS') }}
                  </label>
                  <input
                    v-model.number="storedEvalForm.max_results"
                    type="number"
                    min="1"
                    max="20"
                    class="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  />
                </div>
              </div>
              <div>
                <label
                  class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1"
                >
                  {{ $t('LIBRARIES.EVAL.EXPECTED_VALUE') }}
                  <span
                    v-if="storedEvalForm.method === 'static_contains'"
                    class="text-slate-400 font-normal ml-1"
                  >
                    {{ $t('LIBRARIES.EVAL.ONE_PER_LINE') }}
                  </span>
                </label>
                <textarea
                  v-model="storedEvalForm.value"
                  rows="3"
                  class="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono"
                />
              </div>
              <div class="flex justify-end gap-2">
                <NextButton
                  ghost
                  slate
                  :label="$t('LIBRARIES.EVAL.CANCEL')"
                  @click="cancelForm"
                />
                <NextButton
                  :is-loading="isSavingEval"
                  :label="$t('LIBRARIES.EVAL.SAVE')"
                />
              </div>
            </form>
          </div>

          <!-- Stored Evals List -->
          <div v-if="isFetchingStored" class="text-slate-500 py-4 text-sm">
            {{ $t('LIBRARIES.EVAL.LOADING') }}
          </div>
          <div
            v-else-if="!storedEvals.length && !showCreateForm"
            class="text-slate-500 py-8 text-center text-sm"
          >
            {{ $t('LIBRARIES.EVAL.NO_STORED_EVALS') }}
          </div>
          <table
            v-else-if="storedEvals.length"
            class="w-full woot-table text-sm"
          >
            <thead>
              <tr>
                <th class="text-left text-xs font-medium text-slate-500 pb-2">
                  {{ $t('LIBRARIES.EVAL.NAME') }}
                </th>
                <th class="text-left text-xs font-medium text-slate-500 pb-2">
                  {{ $t('LIBRARIES.EVAL.QUERY') }}
                </th>
                <th class="text-left text-xs font-medium text-slate-500 pb-2">
                  {{ $t('LIBRARIES.EVAL.METHOD') }}
                </th>
                <th class="w-24" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="evalItem in storedEvals"
                :key="evalItem.id"
                class="border-t border-slate-100 dark:border-slate-800"
              >
                <td class="py-2 pr-4">
                  <p class="font-medium text-slate-800 dark:text-slate-200">
                    {{ evalItem.name }}
                  </p>
                  <p v-if="evalItem.description" class="text-xs text-slate-500">
                    {{ evalItem.description }}
                  </p>
                </td>
                <td
                  class="py-2 pr-4 text-slate-600 dark:text-slate-400 max-w-xs truncate"
                >
                  {{ evalItem.query }}
                </td>
                <td class="py-2 pr-4">
                  <span
                    class="px-2 py-0.5 text-xs rounded bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300"
                  >
                    {{ evalItem.expected_outcome?.method || '—' }}
                  </span>
                </td>
                <td class="py-2 text-right">
                  <NextButton
                    v-tooltip="$t('LIBRARIES.EVAL.EDIT_EVAL')"
                    ghost
                    slate
                    icon="i-lucide-pencil"
                    @click="openEditForm(evalItem)"
                  />
                  <NextButton
                    v-tooltip="$t('LIBRARIES.EVAL.DELETE')"
                    ghost
                    slate
                    icon="i-lucide-trash-2"
                    @click="deleteStoredEval(evalItem)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Run Stored Evals Tab -->
        <div v-else-if="activeTab === 'run_stored'">
          <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
            {{ $t('LIBRARIES.EVAL.RUN_STORED_DESCRIPTION') }}
          </p>

          <!-- Select Evals (optional) -->
          <div v-if="storedEvals.length" class="mb-4">
            <p
              class="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2"
            >
              {{ $t('LIBRARIES.EVAL.SELECT_EVALS') }}
            </p>
            <div
              class="space-y-1 max-h-48 overflow-auto border border-slate-200 dark:border-slate-700 rounded-md p-2"
            >
              <label
                v-for="evalItem in storedEvals"
                :key="evalItem.id"
                class="flex items-center gap-2 cursor-pointer py-1 px-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <input
                  type="checkbox"
                  :checked="selectedEvalIds.includes(evalItem.id)"
                  @change="toggleSelectEval(evalItem.id)"
                />
                <span class="text-sm text-slate-800 dark:text-slate-200">
                  {{ evalItem.name }}
                </span>
                <span class="text-xs text-slate-500 ml-auto">
                  {{ evalItem.expected_outcome?.method }}
                </span>
              </label>
            </div>
            <p class="text-xs text-slate-400 mt-1">
              {{ $t('LIBRARIES.EVAL.SELECT_HINT') }}
            </p>
          </div>
          <div
            v-else
            class="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded text-sm text-amber-700 dark:text-amber-300"
          >
            {{ $t('LIBRARIES.EVAL.NO_STORED_EVALS') }}
          </div>

          <div class="flex justify-end">
            <NextButton
              :is-loading="isRunningStored"
              :disabled="!storedEvals.length"
              :label="
                selectedEvalIds.length > 0
                  ? $t('LIBRARIES.EVAL.RUN_SELECTED', {
                      count: selectedEvalIds.length,
                    })
                  : $t('LIBRARIES.EVAL.RUN_ALL')
              "
              @click="runStoredEvals"
            />
          </div>

          <!-- Run Results -->
          <div v-if="runResults" class="mt-6">
            <div
              v-if="runResults.error"
              class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-300 text-sm"
            >
              {{ runResults.error }}
            </div>
            <div v-else class="space-y-4">
              <!-- Summary -->
              <div class="grid grid-cols-4 gap-4">
                <div
                  class="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-md border border-slate-200 dark:border-slate-700 text-center"
                >
                  <p
                    class="text-2xl font-bold text-slate-800 dark:text-slate-200"
                  >
                    {{ runResults.total_evals }}
                  </p>
                  <p class="text-xs text-slate-500">
                    {{ $t('LIBRARIES.EVAL.TOTAL') }}
                  </p>
                </div>
                <div
                  class="p-4 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800 text-center"
                >
                  <p
                    class="text-2xl font-bold text-green-700 dark:text-green-300"
                  >
                    {{ runResults.passed }}
                  </p>
                  <p class="text-xs text-green-600 dark:text-green-400">
                    {{ $t('LIBRARIES.EVAL.PASSED') }}
                  </p>
                </div>
                <div
                  class="p-4 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800 text-center"
                >
                  <p class="text-2xl font-bold text-red-700 dark:text-red-300">
                    {{ runResults.failed }}
                  </p>
                  <p class="text-xs text-red-600 dark:text-red-400">
                    {{ $t('LIBRARIES.EVAL.FAILED') }}
                  </p>
                </div>
                <div
                  class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800 text-center"
                >
                  <p
                    class="text-2xl font-bold text-blue-700 dark:text-blue-300"
                  >
                    {{
                      Math.round((runResults.average_confidence || 0) * 100)
                    }}%
                  </p>
                  <p class="text-xs text-blue-600 dark:text-blue-400">
                    {{ $t('LIBRARIES.EVAL.AVG_CONFIDENCE') }}
                  </p>
                </div>
              </div>

              <!-- Individual Results -->
              <div class="space-y-2">
                <div
                  v-for="result in runResults.results"
                  :key="result.eval_id"
                  class="flex items-center gap-3 p-3 rounded border"
                  :class="
                    result.passed
                      ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                      : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
                  "
                >
                  <span class="text-lg">{{ result.passed ? '✓' : '✗' }}</span>
                  <div class="flex-1 min-w-0">
                    <p
                      class="text-sm font-medium truncate"
                      :class="
                        result.passed
                          ? 'text-green-800 dark:text-green-200'
                          : 'text-red-800 dark:text-red-200'
                      "
                    >
                      {{ result.name }}
                    </p>
                    <p class="text-xs text-slate-500 truncate">
                      {{ result.query }}
                    </p>
                  </div>
                  <div class="text-right shrink-0">
                    <p
                      class="text-sm font-medium text-slate-700 dark:text-slate-300"
                    >
                      {{ Math.round((result.confidence || 0) * 100) }}%
                    </p>
                    <p
                      v-if="result.processing_time_ms"
                      class="text-xs text-slate-400"
                    >
                      {{ result.processing_time_ms }}ms
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
